/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { isFormulaEmpty } from '@gh/builder-shared-data';
import { State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FocusedCell, GridDefinition } from '@gh/core-ui-grid';
import { isAllTrue } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { distinctUntilChanged$, filter$, first$, map$, switchMap$ } from '@gh/rx/operators';
import { isEmpty, isEqual, isNil } from 'lodash';
import { Observable, Subscription } from 'rxjs';

import {
  getJeCuttingListItemId,
  JeCuttingListItem,
  JeElementType,
  JeUomRefItem,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  JeFocusedTable,
  queryJeEditable,
  queryJeElement,
  queryJeFocusedCell,
  queryJeSelectedElementInfo,
  queryJeSelectedItemId,
  queryJeUomRefItem,
} from '../je-details.state';
import { JeDetailsCuttingGridDefinitionFactory } from './je-details-cutting-grid-definition-factory.service';

@Component({
  standalone: false,
  selector: 'gh-je-details-cutting-list',
  templateUrl: 'je-details-cutting-list.component.html',
  styleUrls: ['je-details-cutting-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JeDetailsCuttingGridDefinitionFactory],
})
export class JeDetailsCuttingListComponent implements OnInit, OnDestroy {
  @Input() sourceType: JeElementType;

  sourceId: ID;

  cuttingList$: Observable<JeCuttingListItem[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  total$: Observable<Maybe<number>>;
  editable$: Observable<boolean>;

  gridDefinition: GridDefinition;
  rendered: boolean = false;

  private uom: JeUomRefItem;
  private cuttingListSourceSubscription: Subscription;

  constructor(private changeDetectorRef: ChangeDetectorRef,
              private gridDefinitionFactory: JeDetailsCuttingGridDefinitionFactory,
              private state: State<JeDetailsState>,
              private commands: JeCommandService,
              private ngZone: NgZone) {

  }

  ngOnInit(): void {
    const source$ = this.state.select(queryJeSelectedElementInfo).pipe(
      filter$(Boolean),
      filter$(({ type }) => type === this.sourceType),
      distinctUntilChanged$(isEqual));
    const parent$ = source$.pipe(
      switchMap$((source) => this.state.select(queryJeElement, source.type, source.id)),
      filter$(Boolean));

    this.focusedCell$ = this.state.select(queryJeFocusedCell, JeFocusedTable.CuttingList);
    this.cuttingList$ = parent$.pipe(map$((parent: any) => parent.cuttingList));
    this.total$ = parent$.pipe(map$((parent: any) => isEmpty(parent.cuttingList) ? void 0 : parent.quantity));

    this.editable$ = combineLatest$(
      this.state.select(queryJeEditable),
      this.state.select(queryJeSelectedItemId).pipe(map$(Boolean)), 
      parent$.pipe(map$((parent: any) => isFormulaEmpty(parent.formula)))).pipe(map$(isAllTrue));

    const uom$ = source$.pipe(
      switchMap$(({ id }) => this.state.select(queryJeElement, this.sourceType, id)),
      filter$(Boolean),
      map$(({ uomId }) => this.state.project(queryJeUomRefItem, uomId)));

    this.cuttingListSourceSubscription = combineLatest$(source$, uom$).subscribe(([cuttingListSource, uom]) => {
      if (isNil(uom)) {
        throw new Error('UoM is undefined for this cutting list source');
      }

      this.sourceId = cuttingListSource && cuttingListSource.id;

      this.rerenderIfUomModified(uom);
    });
  }

  ngOnDestroy(): void {
    this.cuttingListSourceSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.cuttingListItemAdd(this.sourceType, this.sourceId);
  }

  onDelete(cutting: JeCuttingListItem): void {
    this.commands.cuttingListItemRemove(this.sourceType, this.sourceId, getJeCuttingListItemId(cutting));
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  /**
   * This method completely redraws grid if uom was changed: destroy it and draw again.
   */
  private rerenderIfUomModified(uom: JeUomRefItem): void {
    if (this.uom !== uom) {
      this.uom = uom;
      if (this.rendered) {
        this.setRendered(false);
        this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => this.render());
      } else {
        this.render();
      }
    }
  }

  private render(): void {
    this.gridDefinition = this.gridDefinitionFactory.create(this.uom);

    this.setRendered(true);
  }

  private setRendered(rendered: boolean): void {
    this.rendered = rendered;
    this.changeDetectorRef.markForCheck();
  }
}
